import React, { useMemo, useState, useRef, useEffect } from "react";
import {
  Box,
  Modal,
  IconButton,
  Button,
  CircularProgress,
} from "@mui/material";
import { keyframes } from "@mui/system";
import CloseIcon from "@mui/icons-material/Close";
import LocalPrintshopOutlinedIcon from "@mui/icons-material/LocalPrintshopOutlined";
import { notifyError } from "../../../../components/ToastContainer/Toast/Toast";

/** Receipt shape used for iframe-only print (avoids 2nd blank page from main app + modal layout). */
type KotReceiptData = {
  brand: string;
  subtitle: string;
  addressLine: string;
  cityState: string;
  orderId: string;
  pickupCode?: string;
  customer: string;
  dateTime: string;
  prepTime: string;
  notes: string;
  items: { name: string; qty: number }[];
  totalItems: number;
};

function escapeHtml(s: string): string {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Minimal HTML for iframe print. Tuned for 76mm thermal: smaller type, lighter weights,
 * tighter spacing, thin dashes, extra left inset (many drivers clip the first few mm).
 */
function buildKotPrintDocumentHtml(data: KotReceiptData): string {
  const prepRow = data.prepTime
    ? `<div class="row"><span class="label">Prep Time:</span><span class="val">${escapeHtml(data.prepTime)}</span></div>`
    : "";
  const pickupRow =
    data.pickupCode && String(data.pickupCode).trim() !== ""
      ? `<div class="pickup-code">Pickup Code: ${escapeHtml(String(data.pickupCode).trim())}</div>`
      : "";
  const itemsHtml = data.items
    .map(
      (it) =>
        `<div class="item-line">${escapeHtml(String(it.qty))} x ${escapeHtml(it.name)}</div>`,
    )
    .join("");
  const itemsBlock =
    data.items.length === 0
      ? `<div class="row muted" style="justify-content:center">No items</div>`
      : itemsHtml;

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1"/>
<title>KOT ${escapeHtml(data.orderId)}</title>
<style>
  @page { margin: 0; size: auto; }
  html, body {
    margin: 0;
    padding: 0;
    width: 76mm;
    max-width: 76mm;
    min-height: 0 !important;
    height: auto !important;
    background: #fff;
    box-sizing: border-box;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  body {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    font-size: 14pt;
    line-height: 1.4;
    font-weight: 400;
    color: #000;
    /* Extra left inset: many thermal drivers clip the first few mm of the printable area */
    padding: 2mm 2mm 2mm 5mm;
    box-sizing: border-box;
    -webkit-font-smoothing: antialiased;
  }
  .center { text-align: center; word-break: break-word; }
  .brand { font-size: 18px; font-weight: 900; letter-spacing: 0.3px; }
  .order-id { margin-top: 2px; font-size: 13px; font-weight: 700; }
  .pickup-code { margin-top: 1px; font-size: 12px; font-weight: 700; }
  .sub { margin-top: 3px; font-size: 14px; font-weight: 800; text-transform: uppercase; }
  .addr { margin-top: 1px; font-size: 12px; font-weight: 400; color: #111; }
  .dash { border-top: 1px dashed #111; margin: 6px 0; }
  .row { display: flex; flex-wrap: wrap; gap: 4px; font-size: 12px; line-height: 1.35; font-weight: 400; }
  .label { font-weight: 400; white-space: nowrap; }
  .val { font-weight: 400; word-break: break-word; min-width: 0; flex: 1 1 auto; }
  .muted { color: #6b7280; font-weight: 400; }
  .items { display: flex; flex-direction: column; gap: 3px; }
  .item-line { font-size: 12px; font-weight: 400; line-height: 1.35; }
  .total { font-size: 13px; font-weight: 400; }
  .pickup-code { font-size: 13px; font-weight: 400; }
  .instruct { margin-top: 2px; font-size: 12px; line-height: 1.35; color: #111; font-weight: 400; }
  .instruct-label { font-weight: 400; color: #111; }
  .instruct-val { color: #111; font-weight: 400; }
  .thanks { margin-top: 6px; text-align: center; font-size: 13px; font-weight: 400; }
</style>
</head>
<body>
  <div class="center">
    <div class="brand">${escapeHtml(data.brand)}</div>
    <div class="order-id">${escapeHtml(data.orderId)}</div>
    <div class="sub">${escapeHtml(data.subtitle)}</div>
    ${data.addressLine ? `<div class="addr">${escapeHtml(data.addressLine)}</div>` : ""}
    ${data.cityState ? `<div class="addr">${escapeHtml(data.cityState)}</div>` : ""}
  </div>
  <div class="dash"></div>
  <div class="row"><span class="label">Customer:</span><span class="val">${escapeHtml(data.customer)}</span></div>
  <div class="row"><span class="label">Date:</span><span class="val">${escapeHtml(data.dateTime)}</span></div>
  ${prepRow}
  <div class="dash"></div>
  <div class="items">${itemsBlock}</div>
  <div class="dash"></div>
  <div class="total">Total Items: ${escapeHtml(String(data.totalItems))}</div>
  <div class="dash"></div>
  ${pickupRow}
  ${pickupRow ? `<div class="dash"></div>` : ""}
  <div class="instruct"><span class="instruct-label">Instructions:</span> <span class="instruct-val">${escapeHtml(data.notes)}</span></div>
  <div class="thanks">THANK YOU!</div>
</body>
</html>`;
}

const slideBlink = keyframes`
  0% { left: -150px; }
  100% { left: 100%; }
`;

const modalStyle = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "calc(100% - 32px)", sm: 460 },
  maxWidth: "92vw",
  bgcolor: "white",
  borderRadius: 2,
  boxShadow: 24,
  p: 0,
  overflow: "hidden",
  maxHeight: "90vh",
  display: "flex",
  flexDirection: "column",
};

interface PrintOrderModalProps {
  open: boolean;
  onClose: () => void;
  order: any | null;
}

const PrintOrderModal: React.FC<PrintOrderModalProps> = ({ open, onClose, order }) => {
  const [printing, setPrinting] = useState(false);
  /** True while `open` is false — user dismissed modal; skip success toast when print finishes later. */
  const modalClosedRef = useRef(!open);

  useEffect(() => {
    modalClosedRef.current = !open;
  }, [open]);

  const previewModel = useMemo(() => {
    if (!order) return null;

    const firstNonEmpty = (...vals: any[]) => {
      for (const v of vals) {
        const s = typeof v === "string" ? v.trim() : v;
        if (s) return s;
      }
      return "";
    };

    const storeName =
      firstNonEmpty(
        (order as any).storeName,
        (order as any).store_name,
        (order as any).store?.name,
        (order as any).store?.store_name,
        (order as any).store_details?.name,
        (order as any).storeDetails?.name,
      ) || "";

    const storeAddressObj =
      (order as any).store?.address ||
      (order as any).store_address ||
      (order as any).storeAddress ||
      null;

    const storeAddressLineFallback = [
      storeAddressObj?.address_line_1,
      storeAddressObj?.address_line_2,
      storeAddressObj?.address_line_3,
      storeAddressObj?.address1,
      storeAddressObj?.address2,
      storeAddressObj?.address3,
      storeAddressObj?.address_1,
      storeAddressObj?.address_2,
      storeAddressObj?.address_3,
    ]
      .map((v: any) => (typeof v === "string" ? v.trim() : v))
      .filter(Boolean)
      .join(", ");

    const storeCityStateFallback = [
      storeAddressObj?.city,
      storeAddressObj?.state,
      storeAddressObj?.pincode,
    ]
      .map((v: any) => (typeof v === "string" ? v.trim() : v))
      .filter(Boolean)
      .join(", ");

    const storeAddressLine =
      firstNonEmpty(
        // prefer already-assembled values (Preparing.tsx / other callers)
        (order as any).storeAddressLine,
        (order as any).store_address_line,
        storeAddressLineFallback,
        (order as any).store?.address_line,
      ) || "";

    const storeCityState =
      firstNonEmpty(
        (order as any).storeCityState,
        (order as any).store_city_state,
        storeCityStateFallback,
        (order as any).store?.city_state,
      ) || "";

    const orderCode = (order as any).order_code || (order as any).orderCode || "N/A";
    const invoiceId = (order as any).id ?? (order as any).invoice_id ?? (order as any).invoiceId;
    const pickupCode =
      firstNonEmpty(
        (order as any).pickup_code,
        (order as any).pickupCode,
        // Some callers may pass nested invoice-like payloads
        (order as any).invoice?.pickup_code,
        (order as any).invoice?.pickupCode,
        Array.isArray((order as any).invoices) ? (order as any).invoices?.[0]?.pickup_code : undefined,
        Array.isArray((order as any).invoices) ? (order as any).invoices?.[0]?.pickupCode : undefined,
      ) || "";
    // flat invoice API: customer is a nested object
    const customerName =
      (order as any).customerName ||
      (order as any).customer_name ||
      (order as any).customer?.name ||
      "N/A";
    const customerPhone =
      (order as any).customerPhone ||
      (order as any).customer_phone ||
      (order as any).customer?.phone ||
      "";
    const createdAt =
      (order as any).fullOrderDate ||
      (order as any).invoice_date ||
      (order as any).created_at ||
      (order as any).createdAt ||
      new Date();

    const itemsSource =
      (order as any).items ??
      (order as any).order_items ?? // Preparing.tsx normalized shape
      (order as any).invoice_items ??
      (order as any).order_items_snapshot ??
      [];
    const items: any[] = Array.isArray(itemsSource) ? itemsSource : [];

    const totalItems = items.reduce(
      (sum: number, it: any) => sum + (Number(it?.quantity ?? 1) || 1),
      0,
    );

    const dateLabel = new Date(createdAt).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    const preparationTimeRaw =
      (order as any).preparation_time ||
      (order as any).preparationTime ||
      "";
    let prepTimeLabel = "";
    if (preparationTimeRaw) {
      const isoMatch = String(preparationTimeRaw).match(/PT(\d+)M/i);
      if (isoMatch) {
        prepTimeLabel = `${isoMatch[1]} mins`;
      } else if (!isNaN(Number(preparationTimeRaw)) && Number(preparationTimeRaw) > 0) {
        prepTimeLabel = `${Number(preparationTimeRaw)} mins`;
      }
    }

    const notes = (
      (order as any).instructions ??
      (order as any).instruction ??   // currentOrder uses singular
      (order as any).notes ??
      (order as any).order_notes ??
      ""
    ).toString().trim();

    return {
      storeName,
      storeAddressLine,
      storeCityState,
      orderCode,
      invoiceId,
      pickupCode,
      customerName,
      customerPhone,
      dateLabel,
      prepTimeLabel,
      notes,
      items,
      totalItems,
    };
  }, [order]);

  const receiptData = useMemo(() => {
    if (!previewModel) return null;
    return {
      brand: "TAZTY",
      subtitle: previewModel.storeName || "AMMAN MESS",
      addressLine: previewModel.storeAddressLine || "",
      cityState: previewModel.storeCityState || "",
      orderId: previewModel.orderCode,
      pickupCode: previewModel.pickupCode || "",
      customer: previewModel.customerName,
      dateTime: previewModel.dateLabel,
      prepTime: previewModel.prepTimeLabel,
      notes: previewModel.notes || "-",
      items: (previewModel.items || []).map((it: any) => ({
        name: it?.product_name || it?.name || "Item",
        qty: Number(it?.quantity ?? 1) || 1,
      })),
      totalItems: previewModel.totalItems,
    };
  }, [previewModel]);

  /** Print only the KOT in a minimal iframe document — avoids a 2nd blank page from the main app + MUI modal. */
  const handlePrint = () => {
    if (!previewModel?.invoiceId) {
      notifyError("Invoice ID not found. Please try again.");
      return;
    }
    if (!receiptData) {
      notifyError("Receipt data not ready.");
      return;
    }
    if (typeof window === "undefined" || !window.print) {
      notifyError("Printing is not supported in this browser.");
      return;
    }

    setPrinting(true);

    const html = buildKotPrintDocumentHtml(receiptData as KotReceiptData);

    const iframe = document.createElement("iframe");
    iframe.setAttribute("title", "KOT print");
    iframe.setAttribute("aria-hidden", "true");
    Object.assign(iframe.style, {
      position: "fixed",
      right: "0",
      bottom: "0",
      width: "0",
      height: "0",
      border: "0",
      opacity: "0",
      pointerEvents: "none",
    });
    document.body.appendChild(iframe);

    const win = iframe.contentWindow;
    if (!win) {
      try {
        document.body.removeChild(iframe);
      } catch {
        /* ignore */
      }
      setPrinting(false);
      notifyError("Print failed.");
      return;
    }

    const doc = win.document;
    doc.open();
    doc.write(html);
    doc.close();

    let finished = false;
    /** Browser timers are numeric IDs; avoid NodeJS.Timeout vs number conflicts in TS. */
    let fallbackTimer: number | undefined;

    const finish = () => {
      if (finished) return;
      finished = true;
      if (fallbackTimer !== undefined) clearTimeout(fallbackTimer);
      try {
        win.removeEventListener("afterprint", onAfterPrint);
      } catch {
        /* ignore */
      }
      try {
        document.body.removeChild(iframe);
      } catch {
        /* ignore */
      }
      setPrinting(false);
      /* No success toast: `afterprint` runs when the system dialog closes for both Print and Cancel
         — browsers do not expose a reliable way to tell which action the user took. */
      if (!modalClosedRef.current) {
        onClose();
      }
    };

    const onAfterPrint = () => finish();

    win.addEventListener("afterprint", onAfterPrint);
    // Some browsers omit afterprint on iframe; avoid leaving a stuck spinner
    fallbackTimer = window.setTimeout(() => {
      if (!finished) finish();
    }, 120_000);

    requestAnimationFrame(() => {
      setTimeout(() => {
        try {
          win.focus();
          win.print();
        } catch {
          if (finished) return;
          finished = true;
          if (fallbackTimer !== undefined) clearTimeout(fallbackTimer);
          try {
            win.removeEventListener("afterprint", onAfterPrint);
          } catch {
            /* ignore */
          }
          try {
            document.body.removeChild(iframe);
          } catch {
            /* ignore */
          }
          setPrinting(false);
        }
      }, 0);
    });
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle} className="pp-modal">
        <style>{`
          .pp-modal * { box-sizing: border-box; }

          .pp-body { flex:1; background: #f5f5f5; padding: 20px; overflow:auto; }
          .receipt-shell { display:flex; justify-content:center; }
          .receipt-card { width: 100%; max-width: 333px; background: #fff; border: 1px solid #e0e0e0; border-radius: 8px; box-shadow: 0 2px 12px rgba(0,0,0,0.07); padding: 16px 14px; font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace; font-size: 15px; line-height: 1.45; color:#111;}
          .r-center { text-align:center; word-break:break-word; overflow-wrap:break-word; }
          .r-brand { font-size: 22px; font-weight: 900; letter-spacing: 0.45px; }
          .r-order-id { margin-top: 4px; font-size: 15px; font-weight: 700; color:#111; }
          .r-sub { margin-top: 5px; font-size: 17px; font-weight: 800; text-transform: uppercase; word-break:break-word; }
          .r-addr { margin-top: 2px; font-size: 14px; color:#111; word-break:break-word; overflow-wrap:break-word; }
          .r-dash { border-top: 2px dashed #111; margin: 10px 0; }
          .r-row { display:flex; justify-content:flex-start; gap:5px; font-size: 14px; line-height: 1.5; flex-wrap:wrap; }
          .r-label { color:#111; font-weight:700; white-space:nowrap; }
          .r-val { color:#111; font-weight:600; word-break:break-word; overflow-wrap:break-word; }
          .r-items { display:flex; flex-direction:column; gap:6px; }
          .r-item-line { font-size: 14px; font-weight:600; color:#111; white-space:pre-wrap; word-break:break-word; }
          .r-total-items { font-size: 16px; font-weight:900; color:#111; }
          .r-pickup-total { font-size: 16px; font-weight:900; color:#111; margin-top: 2px; }
          .r-thanks { margin-top: 8px; text-align:center; font-size: 15px; font-weight:900; }
          .r-instructions { display:flex; flex-direction:column; gap:4px; font-size:14px; margin-top:4px; }
          .r-instructions-label { font-weight:700; color:#111; }
          .r-instructions-val { font-weight:600; color:#111; white-space:pre-wrap; word-break:break-word; }

          .pp-footer { padding: 14px 20px; border-top: 1px solid #e0e0e0; background:#fff; display:flex; flex-direction: column; align-items: stretch; gap: 0; width: 100%; box-sizing: border-box; }
          .pp-btn { height: 40px; border-radius: 4px; font-weight:700; text-transform:none; }
          .pp-btn-dark { background: #000; color: #fff; }
          .pp-btn-dark:hover { background: #333; }

          @media print {
            /* margin:0 + size:auto — fewer phantom extra pages than fixed @page height in Chrome PDF */
            @page {
              margin: 0;
              size: auto;
            }
            /*
              Visibility trick hides the rest of the app. Hidden blocks still reserve height unless
              we collapse flex + remove fixed full-screen modal + min-heights (otherwise page 1 blank).
            */
            /* Document height = content only — helps avoid a blank 2nd page */
            html, body, #root {
              margin: 0 !important;
              padding: 0 !important;
              height: max-content !important;
              min-height: 0 !important;
              max-height: none !important;
              overflow: visible !important;
              background: #fff !important;
            }
            body * { visibility: hidden !important; }
            .receipt-print-area, .receipt-print-area * { visibility: visible !important; }

            /* Backdrop / overlay must not reserve space or clip content */
            .MuiBackdrop-root {
              display: none !important;
            }

            /*
              Critical: screen modal uses translate(-50%,-50%) + 50%/50% — that clips the top in print.
              Reset to normal flow, top-left, zero margin.
            */
            .MuiModal-root {
              position: static !important;
              inset: auto !important;
              top: auto !important;
              left: auto !important;
              right: auto !important;
              bottom: auto !important;
              width: auto !important;
              max-width: none !important;
              display: block !important;
              overflow: visible !important;
              height: max-content !important;
              min-height: 0 !important;
            }
            /* Inner wrapper (focus container) — must not clip in print */
            .MuiModal-root > div:not(.MuiBackdrop-root) {
              position: static !important;
              inset: auto !important;
              overflow: visible !important;
              max-height: none !important;
              height: max-content !important;
              min-height: 0 !important;
              display: block !important;
            }
            .MuiModal-root .MuiBox-root.pp-modal,
            .pp-modal {
              position: static !important;
              top: auto !important;
              left: auto !important;
              right: auto !important;
              bottom: auto !important;
              transform: none !important;
              margin: 0 !important;
              padding: 0 !important;
              max-width: none !important;
              width: auto !important;
              max-height: none !important;
              min-height: 0 !important;
              overflow: visible !important;
              height: max-content !important;
              display: block !important;
              flex: none !important;
              box-shadow: none !important;
              border-radius: 0 !important;
            }

            /* Modal / panel must not clip long receipts */
            .MuiModal-root .MuiBox-root:not(.pp-modal) {
              overflow: visible !important;
              max-height: none !important;
              height: auto !important;
            }
            .pp-body {
              overflow: visible !important;
              max-height: none !important;
              height: max-content !important;
              min-height: 0 !important;
              margin: 0 !important;
              padding: 0 !important;
              flex: none !important;
              flex-grow: 0 !important;
            }

            /* Top-left; 76mm thermal width. Keep KOT on ONE page when it fits (typical orders). */
            .receipt-print-area.receipt-shell {
              display: block !important;
              justify-content: flex-start !important;
            }
            .receipt-print-area.receipt-shell,
            .receipt-print-area {
              position: static !important;
              left: auto !important;
              top: auto !important;
              width: 76mm !important;
              max-width: 76mm !important;
              margin: 0 !important;
              /* Small inset from physical edge; matches 76mm roll */
              padding: 3mm 2mm 3mm 2.5mm !important;
              display: block !important;
              overflow: visible !important;
              max-height: none !important;
              height: max-content !important;
              background: #fff !important;
              box-sizing: border-box !important;
              page-break-inside: avoid !important;
              break-inside: avoid !important;
              page-break-before: auto;
              break-before: auto;
              /* No trailing blank page; prefer single sheet */
              page-break-after: avoid !important;
              break-after: avoid !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color: #000 !important;
              font-size: 14pt !important;
              line-height: 1.45 !important;
            }
            .receipt-card {
              box-shadow: none !important;
              border: none !important;
              border-radius: 0 !important;
              width: 100% !important;
              max-width: 100% !important;
              padding: 0 !important;
              overflow: visible !important;
              max-height: none !important;
              margin: 0 !important;
              page-break-inside: avoid !important;
              break-inside: avoid !important;
              page-break-after: avoid !important;
              break-after: avoid !important;
            }
            .r-items {
              overflow: visible !important;
              max-height: none !important;
            }
            /* Only the receipt prints — avoids blank pages / clipped flow */
            .kot-no-print {
              display: none !important;
            }
            .pp-body.kot-print-body {
              padding: 0 !important;
              background: #fff !important;
              margin: 0 !important;
              page-break-after: avoid !important;
              break-after: avoid !important;
            }
            /* MUI focus / sentinels can reserve a second page in PDF output */
            .MuiModal-root [tabindex="-1"] {
              page-break-after: avoid !important;
              break-after: avoid !important;
              margin: 0 !important;
              padding: 0 !important;
              min-height: 0 !important;
            }
          }
        `}</style>

        <Box
          className="kot-no-print"
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            flexShrink: 0,
            minHeight: 60,
            py: 1.75,
            px: 1.5,
            bgcolor: "transparent",
          }}
        >
          <IconButton
            type="button"
            size="small"
            onClick={onClose}
            aria-label="Close"
            sx={{
              color: "#6b7280",
              bgcolor: "transparent",
              "&:hover": { bgcolor: "rgba(0,0,0,0.06)", color: "#111" },
            }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        <div className="pp-body kot-print-body">
          <div className="receipt-print-area receipt-shell">
            {receiptData ? (
              <div className="receipt-card">
                <div className="r-center">
                  <div className="r-brand">{receiptData.brand}</div>
                  <div className="r-order-id">{receiptData.orderId}</div>
                  <div className="r-sub">{receiptData.subtitle}</div>
                  {receiptData.addressLine && (
                    <div className="r-addr">{receiptData.addressLine}</div>
                  )}
                  {receiptData.cityState && (
                    <div className="r-addr">{receiptData.cityState}</div>
                  )}
                </div>

                <div className="r-dash" />

                <div className="r-row">
                  <div className="r-label">Customer:</div>
                  <div className="r-val">{receiptData.customer}</div>
                </div>
                <div className="r-row">
                  <div className="r-label">Date:</div>
                  <div className="r-val">{receiptData.dateTime}</div>
                </div>
                {receiptData.prepTime && (
                  <div className="r-row">
                    <div className="r-label">Prep Time:</div>
                    <div className="r-val">{receiptData.prepTime}</div>
                  </div>
                )}

                <div className="r-dash" />

                <div className="r-items">
                  {receiptData.items.length === 0 ? (
                    <div className="r-row" style={{ justifyContent: "center", color: "#6b7280" }}>
                      No items
                    </div>
                  ) : (
                    receiptData.items.map((it, idx) => (
                      <div className="r-item-line" key={`${it.name}-${idx}`}>
                        {it.qty} x {it.name}
                      </div>
                    ))
                  )}
                </div>

                <div className="r-dash" />

                <div className="r-total-items">{`Total Items: ${receiptData.totalItems}`}</div>
                <div className="r-dash" />
                {receiptData.pickupCode && (
                  <>
                    <div className="r-pickup-total">{`Pickup Code: ${receiptData.pickupCode}`}</div>
                    <div className="r-dash" />
                  </>
                )}

                <div className="r-instructions">
                  <div className="r-instructions-label">Instructions:</div>
                  <div className="r-instructions-val">{receiptData.notes}</div>
                </div>

                <div className="r-thanks">THANK YOU!</div>
              </div>
            ) : (
              <div style={{ color: "#6b7280", fontSize: 16 }}>Preview not available for this order.</div>
            )}
          </div>
        </div>

        <div className="pp-footer kot-no-print">
          <Button
            fullWidth
            variant="contained"
            onClick={handlePrint}
            disabled={printing || !order || !previewModel?.invoiceId}
            startIcon={
              printing ? null : <LocalPrintshopOutlinedIcon />
            }
            sx={{
              position: "relative",
              overflow: "hidden",
              bgcolor: printing ? "#333" : "#000",
              color: "white",
              borderRadius: 1,
              textTransform: "none",
              fontWeight: 500,
              boxShadow: "none",
              px: 3,
              minWidth: 0,
              width: "100%",
              height: 44,
              transition: "background-color 0.2s",
              "&:hover": { bgcolor: printing ? "#333" : "#000", boxShadow: "none" },
              "&:disabled": { bgcolor: printing ? "#333" : "#ccc", color: "#fff" },
            }}
          >
            {/* Sliding shimmer when idle */}
            {!printing && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  bottom: 0,
                  width: "120px",
                  background: `linear-gradient(
                    90deg,
                    transparent,
                    rgba(255,255,255,0.3) 25%,
                    rgba(255,255,255,0.5) 50%,
                    rgba(255,255,255,0.3) 75%,
                    transparent
                  )`,
                  animation: `${slideBlink} 3.5s linear infinite`,
                  zIndex: 0,
                  pointerEvents: "none",
                }}
              />
            )}
            <Box sx={{ position: "relative", zIndex: 1, display: "flex", alignItems: "center", gap: 1 }}>
              {printing && (
                <CircularProgress size={14} thickness={5} sx={{ color: "white" }} />
              )}
              {printing ? "Opening…" : "Print Now"}
            </Box>
          </Button>
        </div>
      </Box>
    </Modal>
  );
};

export default PrintOrderModal;

